/**
 * Feature flag keys + defaults + metadata.
 * Flag shape on Firestore: settings/featureFlags: { [key]: { enabled, label }, updatedAt, updatedBy }
 */

// Rollout-plan defaults: only the core modules enabled initially.
export const DEFAULT_FLAGS = {
  dashboard:     { enabled: true,  label: 'Dashboard' },
  // RW-2141: behavior flag (NOT a nav surface — absent from PATH_TO_FLAG, and
  // read directly rather than via isFeatureEnabled, so admins get no bypass and
  // see exactly what agents see). OFF = the fourteen-widget dashboard, unchanged.
  // ON = Producer Home: one money band, one "Needs you today" queue, one merged
  // activity feed, one curated rail (the Sept 2026 audit, RW-2141/2143/2144/
  // 2146/2147). Reversible kill-switch — flip back to OFF to restore the old
  // page instantly. Flip via Admin → Feature Flags.
  producerHome:  { enabled: false, label: 'Producer Home dashboard (RW-2141)' },
  myTasks:       { enabled: true,  label: 'My Tasks' },
  // RW-2866: one weekday-morning task digest (in-app + email) in place of the
  // separate deal / onboarding / task reminders. Read SERVER-side (functions*/
  // lib/taskDigestFlag): off = the digest only dry-runs and the old senders
  // run; on = the digest sends and they stand down. Nothing client-side reads it.
  taskDigest:    { enabled: false, label: 'Morning task digest — one summary email + notice (replaces the separate task reminders)' },
  feed:          { enabled: true,  label: 'Feed' },
  groups:        { enabled: true,  label: 'Groups' },
  messages:      { enabled: true,  label: 'Messages' },
  listings:      { enabled: false, label: 'Listings' },
  buyers:        { enabled: false, label: 'Buyers' },
  transactions:  { enabled: false, label: 'Transactions' },
  // RW-883b: data-SOURCE flag (NOT a nav surface — intentionally absent from
  // PATH_TO_FLAG). OFF = compliance review reads/writes Firestore (the default,
  // unchanged path). ON = reads/writes the Postgres complianceApi callable
  // (RW-883a). Reversible: flip back to OFF to fall straight back to Firestore.
  // Flip via Admin → Feature Flags, or settings/featureFlags.compliancePg.enabled=true.
  compliancePg:  { enabled: false, label: 'Compliance on Postgres (RW-883)' },
  // RW-3125: behavior flag (NOT a nav surface — absent from PATH_TO_FLAG). The
  // Broker Review reminders (functions-scheduled complianceReviewNudges): OFF =
  // the morning run only dry-runs and logs what it would send. ON = reviewers
  // get one digest when documents are past the review target, and agents a
  // nudge 1/3/7 days after a return. In-app only.
  complianceNudges: { enabled: false, label: 'Broker Review reminders — overdue reviews and returned documents (RW-3125)' },
  // RW-3880: behavior flag (NOT a nav surface — absent from PATH_TO_FLAG),
  // read SERVER-side only (functions-scheduled complianceReviewNudges). OFF =
  // the weekday-morning run only dry-runs and logs it. ON = an agent with
  // uploads Rêve couldn't confirm as signed, not yet sent for review, gets
  // one in-app digest: "N documents uploaded but not sent for review".
  unsentDocNudges: { enabled: false, label: 'Reminder — documents uploaded but not sent for review (RW-3880)' },
  // RW-919: data-SOURCE flag (NOT a nav surface — absent from PATH_TO_FLAG).
  // OFF = the commission panel on DealDetail is hidden / legacy Firestore engine
  // unchanged. ON = DealDetail shows the Postgres commission panel (commissionApi
  // / app.deal_financials). Reversible: flip back to OFF to hide it instantly.
  // ⚠ BUSINESS-CRITICAL money math — flip only after golden-fixture sign-off.
  commissionPg:  { enabled: false, label: 'Commission on Postgres (RW-919)' },
  // RW-902: behavior flag (NOT a nav surface — absent from PATH_TO_FLAG).
  // OFF = Events page is read-only sync from Google Calendar (the default,
  // unchanged path). ON = admins can create events / invite the team in-app and
  // RSVPs write back to Google Calendar (createTeamEvent + setEventRsvp +
  // syncEventRsvps callables). The server ALSO self-gates on the DWD service
  // account (RW-900): with the flag ON but the SA unconfigured, the write
  // callables throw failed-precondition and the UI shows a "setup pending" state.
  // Reversible kill-switch — flip back to OFF to fall straight back to read-only.
  calendarWriteBack: { enabled: false, label: 'Calendar invites + RSVP write-back (RW-902)' },
  // RW-1786: behavior flag (NOT a nav surface — absent from PATH_TO_FLAG),
  // and like calendarWriteBack it has NO admin bypass (read directly, not via
  // isFeatureEnabled). ON = the two-tier rail: standing banded sub-rail,
  // section click routes to its first item, breadcrumb trail in the topbar.
  // OFF = the pre-Wave-1 flyout behavior (sub-rail on demand, closes on
  // navigate). Rollback lever for the RW-1782 pilot; retire after a release.
  navRail2:      { enabled: true,  label: 'Two-tier nav rail (RW-1786)' },
  pipeline:      { enabled: false, label: 'Pipeline' },
  contacts:      { enabled: false, label: 'Contacts' },
  // RW-2711 "Clean up my book" (Organize). OFF = admins + the pilot list only
  // (settings/featureFlags.contactsCleanup.pilotEmails — checked server-side by
  // contactsApi and aiApi, never on the client). ON = every agent.
  contactsCleanup: { enabled: false, label: 'Contacts · Clean up my book' },
  // RW-494 Rêve Sign — the signature requests module (RW-3307: DocuSeal's
  // "Sent for signature"; DocuSign retired). Nav-surface flag. Admins always
  // see it (isFeatureEnabled short-circuits).
  reveSign:      { enabled: false, label: 'Rêve Sign (signature requests)' },
  calendar:      { enabled: false, label: 'Calendar' },
  ninjaFlow:     { enabled: true,  label: 'Ninja FLOW' },
  // RW-2017: My Numbers (agent reporting, RW-2013). DARK until the module
  // lands — the nav row is gated on this so the shell never shows a route
  // that does not exist yet ("no dead affordances", RW-1783).
  myNumbers:     { enabled: false, label: 'My Numbers (agent reporting)' },
  // RW-1305 Academy — LMS + Coaching as one surface (epic RW-1304). Nav-surface
  // flag: ships OFF until the module's content spine lands; admins always see it
  // (isFeatureEnabled short-circuits). Flip via Admin → Feature Flags.
  academy:       { enabled: false, label: 'Academy' },
  // RW-814 Ask Rêve — AI chat assistant (aiApi callable). Nav-surface flag:
  // ships OFF until the server config (skills + models + caps) is seeded;
  // admins always see it (isFeatureEnabled short-circuits). Flip via
  // Admin → Feature Flags.
  askReve:       { enabled: false, label: 'Ask Rêve' },
  // RW-1502 Buyer Broker Agreements — tracker + expiry warnings + extensions.
  // Nav-surface flag: ships OFF for the rollout window; admins always see it
  // (isFeatureEnabled short-circuits). Flip via Admin → Feature Flags.
  bba:           { enabled: false, label: 'Buyer Agreements' },
  // RW-1610 Flipbooks — branded listing presentations / brochures / buyer
  // packets, shareable as a public link and exportable to PDF. Nav-surface
  // flag: ships OFF for the rollout window; admins always see it
  // (isFeatureEnabled short-circuits). Flip via Admin → Feature Flags.
  // NOTE: this gates the authoring module at /flipbooks only. The public
  // share viewer at /flipbook/:token is deliberately ungated — a client
  // holding a link must never be blocked by an internal rollout flag.
  flipbooks:     { enabled: false, label: 'Flipbooks' },
  // RW-1074 Direction C "One Pipeline" — the unified deal architecture
  // (RW-1074..1100). Behavior + upcoming-surface flag, ships OFF: while OFF,
  // nothing changes anywhere; ON enables the contract-stage wake rule
  // (dealsApi changeStatus auto-instantiates the side's checklist) and, as
  // M2+ land, the Direction C surfaces. Flip via Admin → Feature Flags.
  onePipeline:   { enabled: false, label: 'Deals — One Pipeline (Direction C)' },
  comingSoon:    { enabled: true,  label: 'Coming Soon' },
  buyerNeeds:    { enabled: true,  label: 'Buyer Needs' },
  vendors:       { enabled: false, label: 'Vendors' },
  // RW-2192: behavior flag (NOT a nav surface — absent from PATH_TO_FLAG).
  // ON = Add Vendor shows "Find them on Google Maps" (vendorsApi.searchBusiness,
  // one paid Places API call per click). OFF = the manual form only, nothing
  // else changes. Reversible kill-switch; read directly like calendarWriteBack.
  vendorsPlaces: { enabled: true, label: 'Vendors · Google Maps search (RW-2192)' },
  // RW-861: behavior flag (NOT a nav surface — absent from PATH_TO_FLAG). ON =
  // Settings shows the Dotloop card, where an agent connects their own Dotloop
  // (read-only). Ships OFF; admins see it anyway (isFeatureEnabled bypass) to
  // prove the connect on staging. Dotloop shares loops per agent only, so
  // whether agents ever connect is the RW-860 question, not this flag's.
  dotloop:       { enabled: false, label: 'Dotloop connect in Settings (RW-861)' },
  // RW-3061: kill switches for the AI contract check (RW-3031) — behavior
  // flags, read directly (no admin bypass) by ContractReviewPanel / the doc
  // drawer AND server-side (functions-integrations contractReviewRunner.
  // readSettings, functions contractReviewQueue.checksEnabled). Ship ON; a
  // missing key is on everywhere. aiContractCheck OFF = nothing new is
  // checked and the AI check is hidden; aiContractQuickTerms OFF = no quick
  // key-terms first read (the full check still runs).
  aiContractCheck:      { enabled: true, label: 'AI contract check on deal documents (RW-3031)' },
  aiContractQuickTerms: { enabled: true, label: 'AI contract check · quick key terms first (RW-3061)' },
  // RW-3916 (Clint 10-22): the check RUNNING BY ITSELF when a contract is turned
  // in or filed from a signing — read SERVER-side (contractReviewQueue.
  // autoChecksEnabled, functions-integrations readSettings). Ships OFF and a
  // missing key is OFF there; "Check this" stays (aiContractCheck alone gates it).
  aiContractAutoCheck:  { enabled: false, label: 'AI contract check runs by itself when a contract is turned in (RW-3916)' },
  // RW-3880: kill switch for the AI fallback of the upload signature check,
  // read SERVER-side only (functions uploadSignatureCheck.aiEnabled,
  // functions-integrations signatureCheckRunner.readSettings). Ships ON; a
  // missing key is on. OFF = only the rules decide (DocuSign certificates, PDF
  // signature fields); anything they can't confirm waits for the agent to send.
  aiSignatureCheck:     { enabled: true, label: 'AI signature check on uploads Rêve can’t confirm (RW-3880)' },
  // RW-3307: the DocuSign-era flags (formInstancesDocusign, signComposer,
  // signComposerClassic, signFormsFromFolder, docusignEnvelopeWebhooks) left
  // with DocuSign's screens. All were OFF; their server readers go in RW-3308.
  people:        { enabled: false, label: 'People' },
  offices:       { enabled: false, label: 'Offices' },
  orgChart:      { enabled: true,  label: 'Org Chart' }, // RW-1409: agent-visible (was default-off → deep-links bounced to /dashboard)
  events:        { enabled: false, label: 'Events' },
  documents:     { enabled: false, label: 'Documents' },
  photos:        { enabled: false, label: 'Photos' },
  training:      { enabled: false, label: 'Training' },
  leaderboard:   { enabled: false, label: 'Leaderboard' },
  myProduction:  { enabled: false, label: 'My Production' },
  // RW-991 Connect Extensions — toggleable per surface. Town Halls ships OFF
  // until a video provider is wired; flip on in Admin → Feature Flags.
  connectSearch: { enabled: true,  label: 'Connect Search' },
  saved:         { enabled: true,  label: 'Saved' },
  townHalls:     { enabled: false, label: 'Town Halls' },
  // RW-1642 What's New — the auto-generated changelog (/whats-new + nav badge
  // + callouts). Nav-surface flag: ships OFF for staging QA of the pipeline;
  // admins always see it (isFeatureEnabled short-circuits). Flip via
  // Admin → Feature Flags — and prepare a launch note on this very flag so the
  // log announces itself when it goes live.
  whatsNew:      { enabled: false, label: "What's New" },
  // RW-1680 Onboarding on the task engine — data-SOURCE flag (NOT a nav
  // surface — absent from PATH_TO_FLAG). OFF = toggleOnboardingItemStatus
  // keeps the legacy Firestore items[] path, byte-identical. ON = toggles
  // write the relational checklist engine (app.tasks) first and a one-way
  // mirror re-renders onboardingInstances.items[] for mobile/web readers.
  // Reversible kill-switch: flip OFF to fall straight back to Firestore.
  onboardingEngine: { enabled: false, label: 'Onboarding on task engine (RW-1680)' },
  /* ── RW-2581 rail curation ────────────────────────────────────────────
     Which SECTIONS the left rail shows. These are not module flags: the
     routes underneath stay open and reachable by URL, deep link and nav
     search — turning `railDeals` off hides the Deals section from the rail,
     it does not close /deals. Use the module flag (onePipeline, listings,
     buyers, reveSign, training…) when a module should actually be shut.

     Admins bypass these like any module flag (RW-2624; RW-2581 shipped them
     with no bypass, which hid four sections from every admin). To see
     what a toggle does, an admin switches the header pill to Agent — under
     "Viewing as Agent" isAdmin is false and the curated rail renders. The
     Rail group's `hint` says so in the panel, so a flip that changes
     nothing in Admin view does not read as broken (the RW-2155 trap).

     Shipped OFF for the 2026-09-22 AAC debut: the rail reads Home · Connect
     · FLOW · My Numbers · Company. Any section comes back with one click in
     Admin → Feature Flags — no redeploy. ── */
  railMyDay:     { enabled: false, label: 'Rail · My Day section' },
  railDeals:     { enabled: false, label: 'Rail · Deals section' },
  railAcademy:   { enabled: false, label: 'Rail · Academy section' },
  railSign:      { enabled: false, label: 'Rail · Rêve Sign section' },
  // RW-2896 (P-RW-33): Company › Email Signatures (RW-2917; was a Tools
  // section). Ships OFF; admins see it anyway (isFeatureEnabled bypass) to
  // build and check signatures first.
  emailSignatures: { enabled: false, label: 'Company › Email Signatures' },
};

// Section groupings for the admin UI (match sidebar).
/**
 * Flags the admin panel intentionally does NOT list.
 *
 * FeatureFlagsAdmin renders FLAG_GROUPS, not DEFAULT_FLAGS — a key defined but
 * ungrouped exists, is honoured at runtime, and has no toggle anywhere in the
 * product. RW-2155: `producerHome` shipped that way and the new dashboard was
 * unreachable in prod and staging with nothing to explain why.
 *
 * These two predate that fix and are listed here so the guard test passes
 * deliberately rather than by omission. Neither has been re-examined:
 *   - navRail2   — RW-1786 rollback lever, "retire after a release"
 *   - flipbooks  — routed via PATH_TO_FLAG, never grouped
 * If either should be operable from the admin panel, move it into a group.
 */
export const UNGROUPED_FLAGS = ['navRail2', 'flipbooks'];

/**
 * Routes in PATH_TO_FLAG that NO nav row reaches — the orphan direction.
 *
 * RW-2493: the existing guard walks nav → flag (every navigation.js `flagKey`
 * must equal what the route gate resolves). It says nothing about a route the
 * gate knows and the nav tree does not, so `/photos` sat gated-but-doorless
 * for five months: the sidebar row was deleted as collateral in April
 * (d0d4b2f3) and the `photos` flag was off, each failure hiding the other.
 * PhotosPage took three polish tickets in that window, on a surface no
 * non-admin in prod could open.
 *
 * A route listed here is reachable some OTHER way, and the note says which.
 * Anything not listed must have a navigation.js row at that exact path.
 *   - /home           — alias of /feed, same `feed` flag; the nav row is /feed
 *   - /ask-reve       — reached from the user menu (App.js), flag-gated there
 *                       exactly like the route; deliberately not a rail row
 *   - /my-production  — 🔴 a REAL orphan, same shape as /photos: no nav row
 *                       anywhere, yet `myProduction` is ON in prod. Listed to
 *                       record it, not to bless it — see RW-2509.
 *   - /deals/coming-soon, /deals/buyer-needs — RW-3647: the boards' pre-move
 *                       URLs, reached only by links sent before the move.
 *                       LegacyDeepLinkRedirect forwards them to /connect/…;
 *                       the gate uses the target's flag so it never bounces
 *                       one first.
 */
export const NAVLESS_ROUTES = ['/home', '/ask-reve', '/my-production', '/deals/coming-soon', '/deals/buyer-needs'];

export const FLAG_GROUPS = [
  { label: 'Core', keys: ['dashboard', 'producerHome', 'myTasks', 'taskDigest', 'feed', 'groups', 'messages'] },
  { label: 'Deals', keys: ['listings', 'buyers', 'transactions', 'reveSign', 'compliancePg', 'complianceNudges', 'commissionPg', 'pipeline', 'contacts', 'contactsCleanup', 'bba', 'onePipeline', 'dotloop', 'aiContractCheck', 'aiContractAutoCheck', 'aiContractQuickTerms', 'aiSignatureCheck', 'unsentDocNudges'] },
  { label: 'Plan', keys: ['calendar', 'ninjaFlow', 'myNumbers', 'calendarWriteBack', 'academy', 'askReve'] },
  { label: 'Discover', keys: ['comingSoon', 'buyerNeeds', 'vendors', 'vendorsPlaces'] },
  // Rebase (RW-1680 onto RW-1679): keep this PR's new `onboardingEngine` key,
  // but NOT `onboardingV2` — RW-1679 retired that flag with the v2 surface it
  // gated. This branch predates that deletion; re-adding it here would list a
  // key with no definition.
  { label: 'Company', keys: ['people', 'offices', 'orgChart', 'events', 'documents', 'photos', 'training', 'leaderboard', 'myProduction', 'whatsNew', 'onboardingEngine', 'emailSignatures'] },
  { label: 'Connect', keys: ['connectSearch', 'saved', 'townHalls'] },
  // RW-2581: rail curation — which sections the left rail shows. Grouped
  // (not UNGROUPED_FLAGS) precisely because these are meant to be flipped
  // from the admin panel; an ungrouped key has no toggle anywhere.
  // RW-2624: `hint` renders under the group heading (FeatureFlagsAdmin).
  {
    label: 'Rail',
    keys: ['railMyDay', 'railDeals', 'railAcademy', 'railSign'],
    hint: 'Admin view always shows every section. Switch the header to Agent to see the rail these toggles give agents.',
  },
];

// URL path → flag key mapping, used by App.js for route protection.
export const PATH_TO_FLAG = {
  '/dashboard': 'dashboard',
  '/my-tasks': 'myTasks',
  '/feed': 'feed',
  '/home': 'feed',
  '/groups': 'groups',
  '/messages': 'messages',
  '/listings': 'listings',
  '/flipbooks': 'flipbooks',
  '/buyers': 'buyers',
  '/sign': 'reveSign',
  '/contacts': 'contacts',
  '/calendar': 'calendar',
  '/ninja-flow': 'ninjaFlow',
  '/my-numbers': 'myNumbers',
  '/academy': 'academy',
  '/ask-reve': 'askReve',
  '/buyer-agreements': 'bba',
  // RW-1077 Direction C — the unified pipeline (dark until onePipeline flips).
  '/deals': 'onePipeline',
  // RW-2298: the two boards live under /deals but are their OWN modules — the
  // rail rows gate on comingSoon / buyerNeeds (navigation.js), so the route
  // must too. Without these two keys flagKeyForPath fell to '/deals' →
  // onePipeline (OFF in prod) and every member who clicked a live rail row
  // was bounced to /dashboard. featureFlags.routeGate.test.js asserts every
  // navigation.js flagKey against this table so the pair cannot drift again.
  // RW-3647: the boards now live under /connect. The old /deals/* keys stay
  // so a legacy link is gated by the SAME flag while LegacyDeepLinkRedirect
  // forwards it — without them it would fall to '/deals' → onePipeline and a
  // member with onePipeline OFF would be bounced before the redirect landed.
  '/connect/coming-soon': 'comingSoon',
  '/connect/buyer-needs': 'buyerNeeds',
  '/deals/coming-soon': 'comingSoon',
  '/deals/buyer-needs': 'buyerNeeds',
  '/vendors': 'vendors',
  '/people': 'people',
  '/offices': 'offices',
  '/org-chart': 'orgChart',
  '/events': 'events',
  '/documents': 'documents',
  '/photos': 'photos',
  '/training': 'training',
  '/leaderboard': 'leaderboard',
  '/my-production': 'myProduction',
  // RW-991 Connect Extensions
  '/connect/search': 'connectSearch',
  '/saved': 'saved',
  '/townhalls': 'townHalls',
  // RW-1642 What's New changelog (covers /whats-new/:id via flagKeyForPath's segment fallback)
  '/whats-new': 'whatsNew',
  // RW-2896/2917: Email Signatures (sub-pages resolve by prefix).
  '/email-signatures': 'emailSignatures',
};

/**
 * Look up the flag key for a given path, handling nested routes like
 * /listings/abc. Exact key first, then the LONGEST mapped prefix by whole
 * segment (RW-2298): /deals/coming-soon/abc → '/deals/coming-soon' →
 * comingSoon, not '/deals' → onePipeline. A two-segment key therefore covers
 * its own :id form the way a one-segment key always has (/whats-new/:id).
 * Prefix matching applies to EVERY multi-segment key, not just the two boards:
 * /connect/search/<anything> now resolves to connectSearch where it used to
 * be null (no such route exists today — on record, not a behaviour change).
 * Unmapped paths return null, which isFeatureEnabled treats as allowed.
 */
export function flagKeyForPath(pathname) {
  if (PATH_TO_FLAG[pathname]) return PATH_TO_FLAG[pathname];
  const parts = (pathname || '').split('/').filter(Boolean);
  for (let n = parts.length; n >= 1; n--) {
    const prefix = '/' + parts.slice(0, n).join('/');
    if (PATH_TO_FLAG[prefix]) return PATH_TO_FLAG[prefix];
  }
  return null;
}

/** Is the given feature enabled for the current user? Admins always see everything. */
/**
 * RW-3406: what App.js's route guard does for this path — 'wait', 'allow' or
 * 'bounce' (to /dashboard). It waits for the ROLE as well as the flags: an admin
 * bypasses every flag, and on a cold load (a refresh, a pasted link) isAdmin is
 * false until the user doc's role arrives. Deciding then bounced admins off every
 * flag-off page (prod 10-20: Other Tasks, Academy, Training, Agents & Staff,
 * Leaderboard, Org Chart, Town Halls), and the guard runs again only on a path
 * change, so they stayed bounced. Pure.
 */
export function routeGuardDecision({ path, signedIn, flagsLoaded, roleLoaded, flags, isAdmin }) {
  if (!signedIn || !flagsLoaded || !roleLoaded) return 'wait';
  const key = flagKeyForPath(path);
  if (!key) return 'allow';
  return isFeatureEnabled(key, flags, isAdmin) ? 'allow' : 'bounce';
}

export function isFeatureEnabled(flagKey, flags, isAdmin) {
  if (isAdmin) return true;
  if (!flagKey) return true; // Unknown/unmapped paths are allowed
  const flag = (flags || {})[flagKey];
  if (!flag) return (DEFAULT_FLAGS[flagKey]?.enabled ?? true);
  return flag.enabled !== false;
}

/**
 * RW-2437: legacy paths App.js answers with <Navigate to="/deals…"> (RW-1524
 * retired the modules behind them). A door to one lands on the /deals route
 * guard a moment later, so it bounces exactly like a door to /deals — a
 * "View Transaction →" is a Deals door. Each key is a whole-segment prefix;
 * dealsDoorGuards.test.js checks every key is still redirected in App.js.
 */
// Matched on whole segments here; App.js uses a bare startsWith for the first
// three and an exact match for the last three — no real route falls in between.
export const DEALS_REDIRECTS = {
  '/transactions': '/deals',
  '/my-deals': '/deals',
  '/admin/deals': '/deals',
  '/pipeline': '/deals',
  '/compliance-review': '/deals/review',
  '/pipeline-dashboard': '/deals/leadership',
};

/**
 * RW-2437: the flag the route guard ends up applying to a door's target —
 * flagKeyForPath after the query/hash is dropped (the guard only sees
 * location.pathname; '/deals?x' would otherwise resolve to no flag at all)
 * and a DEALS_REDIRECTS alias is followed.
 */
export function flagKeyForDoor(path) {
  const pathname = String(path || '').split(/[?#]/)[0];
  const alias = Object.keys(DEALS_REDIRECTS).find((a) => pathname === a || pathname.startsWith(`${a}/`));
  return flagKeyForPath(alias ? DEALS_REDIRECTS[alias] : pathname);
}

/**
 * RW-2437: would the route guard let this user open `path`? App.js asks
 * exactly this (flagKeyForPath + isFeatureEnabled, admin bypass included) on
 * every pathname change and bounces a "no" to /dashboard — so a door that
 * links to `path` asks the same question before it renders. `isAdmin` must be
 * the guard's own (the real role with Agent view switched off), as passed down
 * from App.js.
 */
export function isPathEnabled(path, flags, isAdmin) {
  return isFeatureEnabled(flagKeyForDoor(path), flags, isAdmin);
}
